import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useOrg } from "../lib/org";
import { Button, ErrorState, Money, PageHeader, Spinner } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { formatDate } from "../lib/format";
import type { ExpenseListRow } from "../lib/models";
import { useInvoicePilot } from "./InvoicePilotLink";
import { useCorrectionStatus } from './Corrections';

export default function Expenses() {
  const { currentOrg } = useOrg();
  const navigate = useNavigate();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const pilot = useInvoicePilot();
  const statuses=useCorrectionStatus();

  const { data, isLoading, error } = useQuery({
    queryKey: ["expenses", currentOrg?.id],
    queryFn: () => api.get<ExpenseListRow[]>("/expenses"),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader
        title="Expenses"
        subtitle="Direct purchases paid from a bank or card"
        action={<Button onClick={() => navigate("/expenses/new")}>New expense</Button>}
      />
      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <DataTable
          rows={data}
          rowKey={(r) => r.id}
          columns={[
            { key: "date", header: "Date", render: (r) => formatDate(r.txnDate) },
            { key: "num", header: "No.", render: (r) => pilot.data?.enabled ? <button className="text-brand-700 underline" onClick={() => navigate(`/expenses/${encodeURIComponent(r.id)}`)}>{r.docNumber ?? "View expense"}</button> : r.docNumber ?? "—" },
            { key: "payee", header: "Payee", render: (r) => r.payeeName ?? "—" },
            { key: "memo", header: "Memo", render: (r) => statuses.data?.reversed.some(x=>x.target_id===r.id)?'Reversed — original retained':r.memo ?? "—" },
            {
              key: "total",
              header: "Total",
              align: "right",
              render: (r) => <Money cents={r.total} currency={currency} />,
            },
          ]}
        />
      )}
    </div>
  );
}
